import { useSyncExternalStore } from 'react'
import { getPwaSnapshot, subscribePwa } from './pwaState'

/** Live view of the install / update state of the web app. */
export function usePwa() {
  const state = useSyncExternalStore(subscribePwa, getPwaSnapshot, getPwaSnapshot)
  return { ...state }
}

/**
 * Manual install guides, for browsers that never offer a native install window (Safari, Firefox)
 * or that did not offer one this time. `buttonLabel` replaces the generic button text.
 */
const GUIDES = {
  'ios-safari': {
    title: 'Add M1NT to your Home Screen',
    buttonLabel: 'Add to Home Screen',
    steps: [
      'Tap the Share button in the Safari toolbar.',
      'Scroll down and choose "Add to Home Screen".',
      'Tap Add. M1NT then opens full screen from your home screen.',
    ],
  },
  'ios-other': {
    title: 'Add M1NT to your Home Screen',
    buttonLabel: 'Add to Home Screen',
    steps: [
      'Open the Share menu of your browser.',
      'Choose "Add to Home Screen".',
      'Tap Add. M1NT then opens full screen from your home screen.',
    ],
    note: 'Do not see it? Open this page in Safari instead.',
  },
  'safari-mac': {
    title: 'Add M1NT to your Dock',
    buttonLabel: 'Add to Dock',
    steps: [
      'In the menu bar, choose File, then "Add to Dock". You can also click the Share button in the toolbar.',
      'Keep the name M1NT and click Add.',
      'Open M1NT from your Dock or Launchpad. It gets its own window.',
    ],
    note: 'Needs Safari 17 on macOS Sonoma (14) or later.',
  },
  firefox: {
    title: 'Install from Chrome or Edge',
    steps: [
      'Firefox cannot install web apps on a computer.',
      'Open this page in Chrome or Edge.',
      'Choose "Install on this computer".',
    ],
  },
  android: {
    title: 'Add M1NT to your home screen',
    buttonLabel: 'Add to Home screen',
    steps: [
      'Open the browser menu (three dots).',
      'Choose "Install app" or "Add to Home screen".',
      'Confirm with Install.',
    ],
  },
  edge: {
    title: 'Install M1NT from Edge',
    steps: [
      'Open the Edge menu (three dots).',
      'Choose "Apps", then "Install this site as an app".',
      'Confirm with Install.',
    ],
  },
  chrome: {
    title: 'Install M1NT from Chrome',
    steps: [
      'Open the Chrome menu (three dots).',
      'Choose "Cast, save and share", then "Install page as app".',
      'Confirm with Install.',
    ],
  },
}

function detectBrowser() {
  const ua = navigator.userAgent || ''
  const isIos =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  const isSafari = /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(ua)
  if (isIos) return isSafari ? 'ios-safari' : 'ios-other'
  if (isSafari) return 'safari-mac'
  if (/android/i.test(ua)) return 'android'
  if (/firefox/i.test(ua)) return 'firefox'
  if (/edg/i.test(ua)) return 'edge'
  return 'chrome'
}

/** Step-by-step install guide that fits the current browser, or null when it cannot be told. */
export function getInstallGuide() {
  if (typeof navigator === 'undefined') return null
  return GUIDES[detectBrowser()] || null
}

/** The same guide as one sentence, for tight spaces such as the navigation drawer. */
export function getInstallHint() {
  const guide = getInstallGuide()
  return guide ? guide.steps.join(' ') : null
}
